import React, { useState, useContext, useCallback, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../api/axios";
import AuthContext from "../context/AuthContext";
import { useDropzone } from "react-dropzone";
// import { useVideoPolling } from "../hooks/useVideoPolling";
import {
  getPresign,
  finalizeUpload,
  uploadToS3,
  pollUntilReady,
  createUserPost,
  createThreadPost,
} from "../api/PostAPI";
import TagInput from "./TagInput";
import MentionSuggestions from "./mentions/MentionSuggestions";
import { useMentionAutocomplete } from "../hooks/useMentionAutocomplete";
import { mentionLimitMessage } from "../utils/mentions";

// Mirrors backend Post.EXPIRATION_CHOICES (blorp/models/posts.py) exactly.
const EXPIRATION_OPTIONS = [
  { value: "24_hours", label: "24", ariaLabel: "24 hours" },
  { value: "72_hours", label: "72", ariaLabel: "72 hours" },
  { value: "indefinite", label: "∞", ariaLabel: "Indefinite" },
];
const DEFAULT_EXPIRATION = "72_hours";

function PostUploadForm({ onUploadSuccess, e_id }) {
  const { token } = useContext(AuthContext);
  // media
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const textRef = useRef(null);
  const mention = useMentionAutocomplete({ value: text, onValueChange: setText, inputRef: textRef });
  const [mediaType, setMediaType] = useState("text");
  const [rotation, setRotation] = useState(0);
  const [videoFile, setVideoFile] = useState(null);
  const [videoAsset, setVideoAsset] = useState(null);
  // location
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  // tags
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const maxTags = 5;
  // expiration
  const [expiration, setExpiration] = useState(DEFAULT_EXPIRATION);
  // status
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  // env
  const APP_ENV = process.env.REACT_APP_ENV;
  const isProd = APP_ENV === "production";

  function getPosition(options) {
    return new Promise((resolve, reject) =>
      navigator.geolocation
        ? navigator.geolocation.getCurrentPosition(resolve, reject, options)
        : reject(new Error("Geolocation not supported"))
    );
  }

  // PROD
  async function handleVideoUpload(file, setProgress, setStatus) {
    try {
      setStatus("Requesting upload URL…");
      const presign = await getPresign(file, token);

      setStatus("Uploading to S3…");
      await uploadToS3({
        upload_url: presign.upload_url,
        headers: presign.headers,
        file,
        onProgress: setProgress,
      });

      setStatus("Finalizing…");
      const { id } = await finalizeUpload({ key: presign.key, file }, token);

      setStatus("Processing…");
      const finalData = await pollUntilReady({ id }, token);

      setVideoFile({ preview: finalData.preview_url, name: file.name });
      setVideoAsset(finalData);
      setStatus("Done!");
      return finalData;
    } catch (err) {
      console.error(err);
      setStatus(err.message || "Upload failed");
      throw err;
    }
  }

  const onDrop = useCallback((acceptedFiles) => {
    // GUARDRAILS
    if (acceptedFiles.length === 0) return;

    const maxSize = 100 * 1024 * 1024; // 100 MB

    acceptedFiles.forEach((file) => {
      if (file.size > maxSize) {
        alert("File too large! Max size is 100 MB.");
        return;
      }
    });
    //

    // only accepts one file currently
    const file = acceptedFiles[0];

    // DEV ONLY
    const handlePreviewUpload = async (file) => {
      setLoading(true);
      const formData = new FormData();
      formData.append("media", file);
      formData.append("media_type", type);

      try {
        const res = await axiosInstance.post(`/posts/preview/`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Token ${token}`,
          },
        });
        const finalData = await pollUntilReady({ id: res.data.video_id }, token);
        setVideoFile({ preview: finalData.preview_url });
        // setMediaId(res.data.video_id);
      } catch (err) {
        console.error("Error uploading preview", err);
      } finally {
        setLoading(false);
      }
    };

    let type = "image";
    if (file.type.startsWith("image")) {
      type = "image";
    } else if (file.type.startsWith("video")) {
      type = "video";
      if (isProd) {
        handleVideoUpload(file, setProgress, setStatus); // PROD
      } else {
        handlePreviewUpload(file); // DEV ONLY
      }
    }

    // acceptedFiles checks if the upload meets size requirement e.g. 100MB
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
    );

    setMediaType(type);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [], // Accept all images
      "video/*": [], // Accept all video types
    },
    multiple: false, // Change to true if you want multiple
  });

  // TODO: bug causes getting position data to remove whatever parts of the form were filled out.
  async function getGeoCoords() {
    try {
      const pos = await getPosition({ enableHighAccuracy: true, timeout: 8000 });
      setLatitude(pos.coords.latitude);
      setLongitude(pos.coords.longitude);
    } catch (e) {
      console.warn("No geolocation", e?.message);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setOk(false);

    const formData = new FormData();

    if (mediaType === "video" && isProd) {
      if (!videoAsset?.video_id) {
        setError("Video is still processing or missing.");
        setSubmitting(false);
        return;
      }
      formData.append("video_id", String(videoAsset.video_id));
    } else if (files.length > 0) {
      formData.append("media", files[0]);
    }

    formData.append("media_type", mediaType);
    formData.append("text", text);
    formData.append("rotation", rotation);
    formData.append("expiration_type", expiration);
    tags.forEach((tag) => {
      formData.append("tags", tag);
    });

    if (Number.isFinite(latitude)) {
      formData.append("latitude", String(latitude));
    }
    if (Number.isFinite(longitude)) {
      formData.append("longitude", String(longitude));
    }

    setLoading(true);

    try {
      const res = await createUserPost(formData, token);
      const { data } = res;

      setOk(true);
      setText("");
      setMediaType("text");
      setFiles([]);
      setVideoFile(null);
      setVideoAsset(null);
      setTags([]);
      setExpiration(DEFAULT_EXPIRATION);

      onUploadSuccess?.(data);
    } catch (err) {
      console.error(err);
      const msg =
        mentionLimitMessage(err) ??
        err.response?.data?.detail ??
        (err.response?.data && JSON.stringify(err.response.data)) ??
        err.message;
      setError(String(msg));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      {ok && <div className="mb-3 rounded bg-green-50 p-3 text-green-700">Posted!</div>}
      {error && <div className="mb-3 rounded bg-red-50 p-3 text-red-700">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Media */}
        <div>
          <label className="block text-sm mb-1">Media</label>

          <div
            {...getRootProps()}
            className={`w-full border rounded px-3 py-8 text-center cursor-pointer ${
              isDragActive ? "bg-gray-50" : "bg-white"
            }`}
          >
            <input {...getInputProps()} />
            <p className="text-sm text-gray-600">
              {isDragActive
                ? "Drop the file here…"
                : "Drag & drop an image/video, or click to select"}
            </p>
            <p className="text-xs text-gray-400 mt-1">Supported: images and mp4 videos</p>

            {(files.length > 0 || videoFile) && (
              <div className="mt-4 space-y-3">
                {files.map((file, idx) => (
                  <div key={idx} className="border rounded p-2">
                    {file.type.startsWith("image/") && (
                      <img
                        src={file.preview}
                        alt="Preview"
                        className="w-full max-h-[60vh] object-contain rounded"
                      />
                    )}
                    <p className="text-xs text-gray-500 mt-2">{file.name}</p>
                  </div>
                ))}

                {videoFile && (
                  <div className="border rounded p-2">
                    <video
                      src={videoFile.preview}
                      controls
                      className="w-full max-h-[60vh] object-contain rounded"
                      style={{ transform: `rotate(${rotation}deg)` }}
                    />
                    <p className="text-xs text-gray-500 mt-2">{videoFile.name}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Video rotate controls */}
          {mediaType === "video" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <button
                type="button"
                className="w-full border rounded px-3 py-2 hover:bg-gray-50"
                onClick={() => setRotation((prev) => (prev + 90) % 360)}
              >
                Rotate Right
              </button>
              <button
                type="button"
                className="w-full border rounded px-3 py-2 hover:bg-gray-50"
                onClick={() => setRotation((prev) => (prev + 270) % 360)}
              >
                Rotate Left
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm mb-1">Text</label>
          <textarea
            ref={textRef}
            value={text}
            {...mention.inputProps}
            onKeyDown={mention.handleKeyDown}
            aria-autocomplete={mention.ariaProps["aria-autocomplete"]}
            aria-controls={mention.ariaProps["aria-controls"]}
            aria-activedescendant={mention.ariaProps["aria-activedescendant"]}
            placeholder="Write something…"
            className="w-full border rounded px-3 py-2"
            rows={4}
          />
          {mention.suggestions && <MentionSuggestions {...mention.suggestions} />}
        </div>

        <TagInput tags={tags} setTags={setTags} />

        {/* Location */}
        {/* <div>
          <label className="block text-sm mb-1">Location</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              className="w-full border rounded px-3 py-2 hover:bg-gray-50"
              onClick={() => {
                setLatitude(null);
                setLongitude(null);
              }}
            >
              Disable
            </button>

            <button
              type="button"
              className="w-full border rounded px-3 py-2 hover:bg-gray-50"
              onClick={() => getGeoCoords()}
            >
              Enable
            </button>
          </div>

          {latitude && longitude && (
            <p className="text-xs text-gray-500 mt-2">
              Using location: {latitude}, {longitude}
            </p>
          )}
        </div> */}

        {/* Expiration */}
        <div>
          <label className="block text-sm mb-1">Post duration</label>
          <div
            role="radiogroup"
            aria-label="Post duration"
            className="inline-flex rounded-full border border-gray-300 bg-gray-50 p-1"
          >
            {EXPIRATION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={expiration === opt.value}
                aria-label={opt.ariaLabel}
                onClick={() => setExpiration(opt.value)}
                className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
                  expiration === opt.value
                    ? "bg-black text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-full bg-black text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? "Uploading…" : "Post"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PostUploadForm;
